// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { IState } from "states/states";
import {
  ActionType,
  IAction,
  IActionTypeSelectDashboardItem,
  IActionTypeSelectGuiItem,
  raiseAction,
} from "actions";
import { IEnergyMeter, ITariffCircuit } from "parameter";
import { getGraphBoundaryDates, formatPowerValue, formatEnergyValue } from "guiutils";
import { isSet, roundToDigits } from "utils";
import { TimePeriod } from "types";

import ProgressBar from "./ProgressBar";

const DIGITS_TO_SHOW = 2;

interface ITariffCircuitExtProps {
  id: number;
  maxValue?: number;
  isRelatedToTargetValue: boolean;
  relatedDateString: string;
  showHierachy: boolean;
  showCategory: boolean;
  value?: number;
  trendMember?: "energy" | "power" | "costs";
  filterText?: string;
  selectedDashboardIndex: number;
  selectedHierarchyId: number | null | undefined;
  selectedCategory: string | null | undefined;
  color: string;
  disabled?: boolean;
}

interface ITariffCircuitProps {
  tariffCircuits: ITariffCircuit[];
  energymeters: IEnergyMeter[];
  parameter: ITariffCircuit | undefined;
  period: TimePeriod;
  startDate: number | undefined;
  endDate: number | undefined;
  isAtEnd: boolean;
  trendMember: "energy" | "power" | "costs";
  currency: string;
}

const mapStateToProps = (state: IState, ownProps: ITariffCircuitExtProps) => {
  const curPeriod = state.gui.trendPeriod;
  const graphDates = getGraphBoundaryDates(
    curPeriod.startDate,
    curPeriod.endDate,
    state.site.tzOffset,
    curPeriod.timePeriod,
  );
  const parameter = state.parameter?.tariffline.find(
    (curItem, curIndex) => (curItem.id ?? curIndex) === ownProps.id,
  );
  const props: ITariffCircuitProps = {
    tariffCircuits: state.parameter?.tariffline ?? [],
    energymeters: state.parameter?.energymeter ?? [],
    parameter,
    period: curPeriod.timePeriod,
    startDate: graphDates.startDate,
    endDate: graphDates.endDate,
    isAtEnd: !isSet(curPeriod.startDate),
    trendMember: isSet(ownProps.trendMember) ? ownProps.trendMember : state.gui.trendMember,
    currency: "EUR",
  };
  return props;
};

interface ITariffCircuitDispatch {
  onHierarchyChanged: (index: number, hierarchyId: number | null | undefined) => void;
  onCategoryChanged: (dashboardIndex: number, selection: string | undefined) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITariffCircuitDispatch = {
    onHierarchyChanged: (index: number, hierarchyId: number | null | undefined) => {
      const payload: IActionTypeSelectDashboardItem = { index, hierarchyId };
      dispatch(raiseAction(ActionType.ACT_SELECT_HIERARCHY, payload));
    },
    onCategoryChanged: (dashboardIndex: number, selection: string | undefined) => {
      const payload: IActionTypeSelectGuiItem = { dashboardIndex, selection };
      dispatch(raiseAction(ActionType.ACT_SELECT_CATEGORY, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  relatedTo: {
    id: "related to: ",
  },
  energyMeters: {
    id: "Energy Meters",
  },
  targetValue: {
    id: "Target Value",
  },
  noDataAvailable: {
    id: "No data available.",
  },
});

class TariffCircuitComponent extends React.Component<
  ITariffCircuitProps & ITariffCircuitExtProps & ITariffCircuitDispatch & WrappedComponentProps
> {
  public render() {
    const parent = this.props.showHierachy
      ? this.props.tariffCircuits.find((curItem) => curItem.id === this.props.parameter?.parent)
      : undefined;
    const emContributions = this.props.parameter?.contributions?.map((curContr) => curContr.em);
    const energyMeters = this.props.energymeters
      .filter((curItem) => isSet(curItem.id) && emContributions?.includes(curItem.id))
      .map((curItem) => curItem.name);
    const tooltipMeters = energyMeters
      ? `${this.props.intl.formatMessage(messages.energyMeters)}: ${energyMeters.join(", ")}`
      : undefined;
    const tariffCircuit = this.props.tariffCircuits.find(
      (curItem, curIndex) => (curItem.id ?? curIndex) === this.props.id,
    );

    const valueText =
      this.props.trendMember === "costs"
        ? `${isSet(this.props.value) ? roundToDigits(this.props.value, DIGITS_TO_SHOW) : "-"} ${
            this.props.currency
          }`
        : this.props.trendMember === "energy"
        ? formatEnergyValue(this.props.value, tariffCircuit?.unit)
        : formatPowerValue(this.props.value, tariffCircuit?.unit);

    const maxValueText =
      this.props.trendMember === "costs"
        ? `${roundToDigits(this.props.maxValue, DIGITS_TO_SHOW)} ${this.props.currency}`
        : this.props.trendMember === "energy"
        ? formatEnergyValue(this.props.maxValue, tariffCircuit?.unit)
        : formatPowerValue(this.props.maxValue, tariffCircuit?.unit);

    const percentage = Math.abs(((this.props.value ?? 0) * 100) / (this.props.maxValue ?? 1));

    const tooltipPower =
      isSet(this.props.value) && !isNaN(this.props.value)
        ? `${this.props.intl.formatMessage(messages.relatedTo)}${maxValueText}\n${
            this.props.isRelatedToTargetValue
              ? `${this.props.intl.formatMessage(messages.targetValue)} [${
                  this.props.relatedDateString ?? ""
                }]`
              : `${this.props.relatedDateString ?? ""}`
          }`
        : `${this.props.intl.formatMessage(messages.noDataAvailable)}`;

    const tooltip = `${tooltipPower}\n\n${tooltipMeters}`;

    const value = valueText.split(" ")[0];
    const unit = valueText.split(" ").length > 1 ? valueText.split(" ")[1] : "-";

    return (
      <tr
        className={`tariffcircuititem ${this.props.disabled === true ? "disabled" : ""} ${
          this.props.selectedHierarchyId !== this.props.id ? "" : "selectedHierarchy"
        }`}
      >
        <td>
          <div
            style={{
              background: this.props.color,
            }}
          />
          {this.props.parameter?.name}
        </td>
        {this.props.showHierachy && (
          <td
            className={`optional ${this.props.selectedHierarchyId !== parent?.id ? "link" : ""}`}
            onClick={() => {
              isSet(parent) &&
                this.props.onHierarchyChanged(this.props.selectedDashboardIndex, parent.id);
            }}
          >
            {parent?.name}
          </td>
        )}
        {this.props.showCategory && (
          <td
            className={`optional ${
              (this.props.parameter?.categories?.length ?? 0) > 0 &&
              this.props.parameter?.categories?.[0] !== "" &&
              this.props.parameter?.categories?.[0] !== this.props.selectedCategory
                ? "link"
                : ""
            }`}
            onClick={() => {
              isSet(this.props.parameter?.categories?.[0]) &&
                this.props.onCategoryChanged(
                  this.props.selectedDashboardIndex,
                  this.props.parameter.categories?.[0],
                );
            }}
          >
            {this.props.parameter?.categories?.[0]}
          </td>
        )}
        <td className="value-col">
          {isSet(this.props.value) && !isNaN(this.props.value) ? (
            <ProgressBar
              value={value}
              unit={unit}
              percentage={percentage}
              tooltip={tooltip}
              disabled={this.props.disabled}
            ></ProgressBar>
          ) : (
            <></>
          )}
        </td>
      </tr>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(TariffCircuitComponent));
